import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { RootState } from '../../../store';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { FLOW_RULES, RuleChoice, RuleKind } from '../utils/flowRules';
import { choiceOf, defaultChoices, loadChoices, saveChoices } from '../utils/flowRuleSettings';

/**
 * 연결 규칙 설정 — 금지 규칙(1안 막고 알림 / 2안 사이에 노드를 넣도록 안내)과 주의 규칙(1안 잇고 알림 / 2안 잇기 전에 확인)을
 * 규칙마다 고른다. 사용자별로 이 브라우저에 저장된다(utils/flowRuleSettings).
 */
const FlowRulesDialog: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();
  useEscapeKey(onClose);
  const loginId = useSelector((s: RootState) => s.auth.user?.loginId);
  const [choices, setChoices] = useState(() => loadChoices(loginId));

  const set = (id: string, c: RuleChoice) => {
    const next = { ...choices, [id]: c };
    setChoices(next);
    saveChoices(loginId, next);
  };
  const reset = () => { const d = defaultChoices(); setChoices(d); saveChoices(loginId, d); };

  const section = (kind: RuleKind) => (
    <div className="space-y-2">
      <div className="text-xs font-semibold text-gray-600">{t(`workflow:flowRule.${kind}Title`)}</div>
      {FLOW_RULES.filter((r) => r.kind === kind).map((r) => {
        const cur = choiceOf(r, choices);
        return (
          <div key={r.id} className="border border-gray-200 rounded px-3 py-2">
            <div className="text-sm font-medium text-gray-800">{t(`workflow:flowRule.${r.id}.name`)}</div>
            <div className="text-xs text-gray-500 mt-0.5">{t(`workflow:flowRule.${r.id}.desc`)}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs">
              {r.options.length === 1 ? (
                <span className="text-gray-500">{t('workflow:flowRule.fixed')}</span>
              ) : r.options.map((o) => (
                <label key={o} className="inline-flex items-center gap-1 cursor-pointer text-gray-700">
                  <input type="radio" name={`flowRule-${r.id}`} checked={cur === o} onChange={() => set(r.id, o)} />
                  {t(o === 'opt1' ? 'workflow:flowRule.optLabel1' : 'workflow:flowRule.optLabel2')}
                  {' '}{t(kind === 'forbidden' ? `workflow:flowRule.${r.id}.${o}` : `workflow:flowRule.caution${o === 'opt1' ? 'Opt1' : 'Opt2'}`)}
                  {o === r.defaultChoice && <span className="text-gray-400">{t('workflow:flowRule.default')}</span>}
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-30" onMouseDown={onClose}>
      <div className="bg-white rounded shadow-lg w-[560px] max-w-[95vw] max-h-[85vh] flex flex-col" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
          <div>
            <div className="text-sm font-semibold text-gray-800">{t('workflow:flowRule.title')}</div>
            <div className="text-xs text-gray-500">{t('workflow:flowRule.intro')}</div>
          </div>
          <button className="px-2 py-1 text-xs rounded hover:bg-gray-100 text-gray-600" onClick={onClose} title={t('common:ui.close')}>✕</button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto">
          {section('forbidden')}
          {section('caution')}
        </div>
        <div className="px-4 py-2 border-t border-gray-200 flex justify-between shrink-0">
          <button className="px-3 py-1 text-xs rounded border border-gray-300 hover:bg-gray-100 text-gray-700" onClick={reset}>{t('workflow:flowRule.reset')}</button>
          <button className="px-3 py-1 text-xs rounded bg-blue-600 hover:bg-blue-700 text-white" onClick={onClose}>{t('common:ui.close')}</button>
        </div>
      </div>
    </div>
  );
};

export default FlowRulesDialog;
